import translate from '@allesauto/i18n';
import {
  FieldTypeAsset,
  HeadlineVariation,
  PRESS_ITEMS_PER_PAGE,
} from '@allesauto/utils/consts';
import { formatDateToShortGermanDate } from '@allesauto/utils/funcs';
import { Download16 } from '@carbon/icons-react';
import { sbEditable } from '@storyblok/storyblok-editable';
import clsx from 'clsx';
import React, { FC, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';

import Headline from '../headline';
import NextImage from '../nextImage';
import Pagination from '../pagination';
import TextLink from '../textLink';
import {
  getPressPagination,
  savePressPagination,
} from '../../../../../apps/web/src/store/redux/slices/pages/press/index';

export interface IPressComponent {
  pressComponents: PressComp[];
  cssClass?: string;
  component: string;
  _uid: string;
  _editable?: string;
}

interface PressComp {
  _uid: string;
  image: FieldTypeAsset;
  date: string;
  title: string;
  bodyText: string;
  pdfText: string;
  pdfLink: FieldTypeAsset;
}

const PressComponent: FC<IPressComponent> = (props) => {
  const { pressComponents, cssClass } = props;
  const intl = useIntl();
  const dispatch = useDispatch();
  const pageNumber: number = useSelector(getPressPagination);

  const noResults = pressComponents.length > 0;
  const pagesCount = Math.ceil(pressComponents.length / PRESS_ITEMS_PER_PAGE);
  const slicedValues = pressComponents.slice(
    (pageNumber - 1) * PRESS_ITEMS_PER_PAGE,
    (pageNumber - 1) * PRESS_ITEMS_PER_PAGE + PRESS_ITEMS_PER_PAGE
  );

  const classCss = {
    wrapper: clsx('py-80px', cssClass),
    firstRow: 'sm:col-start-3 sm:col-end-22 lg:col-start-2 sm:col-end-16',
    secondRow: 'grid grid-cols-24 mb-80px',
    imageWrapper:
      'relative sm:col-start-3 sm:col-end-23 lg:col-start-2 lg:col-end-12 aspect-video',
    newsWrapper:
      'relative sm:col-start-3 sm:col-end-23 lg:col-start-14 lg:col-end-23',
    newsDateClass: 'text-grey100 text-p4',
    newsBodyClass: 'text-grey100 text-p4',
  };

  const handlePageClick = useCallback(
    (n: number) => {
      dispatch(savePressPagination(n));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [dispatch]
  );

  return (
    <>
      <div {...sbEditable(props)} className={classCss.wrapper}>
        <div className={classCss.secondRow}>
          <div className={classCss.firstRow}>
            <Headline
              headlineText={translate(intl, 'ui.PRESS.WE_HAVE_RESULTS')}
              headlineTag={HeadlineVariation.H2}
              className="text-grey100"
            />
          </div>
        </div>
        {noResults &&
          slicedValues.map((pressItem) => {
            return (
              <div className={classCss.secondRow} key={pressItem._uid}>
                <div className={classCss.imageWrapper}>
                  <NextImage
                    useCustomLoader={false}
                    src={pressItem.image.filename}
                    layout="fill"
                    objectFit="cover"
                  />
                </div>
                <div className={classCss.newsWrapper}>
                  <p className={classCss.newsDateClass}>
                    {formatDateToShortGermanDate(pressItem.date)}
                  </p>
                  <Headline
                    headlineText={pressItem.title}
                    headlineTag={HeadlineVariation.H4}
                    className="py-30px text-grey100"
                  />
                  <p className={classCss.newsBodyClass}>{pressItem.bodyText}</p>
                  <TextLink
                    text={pressItem.pdfText}
                    href={pressItem.pdfLink.filename}
                    icon={<Download16 />}
                    customClassName="pt-20px"
                    target="_blank"
                  />
                </div>
              </div>
            );
          })}
        {pagesCount > 1 && (
          <Pagination
            pagesCount={pagesCount}
            activePage={pageNumber}
            onPageClick={(n) => handlePageClick(n)}
            pagesVisibleCountLg={PRESS_ITEMS_PER_PAGE}
          />
        )}
      </div>
    </>
  );
};

export default PressComponent;
